import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Controller,
  FieldArrayWithId,
  useFieldArray,
  UseFieldArrayInsert,
  UseFieldArrayRemove,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { OperatingHoursTimespan } from 'features/locations/location.types';
import { getDayOfWeek } from 'shared/dates';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  formatTimeZoneDisplayName,
  isValidTimeZone,
} from 'features/locations/utils/operatingHours/timeZone';
import { TimeField } from './TimeField';

type FormHoursProps = {
  locationTimeZone?: string;
};

export type FormValues = {
  expandHours: boolean;
  hours: OperatingHoursTimespan[];
};

type HourFieldProps = {
  hourField: FieldArrayWithId<FormValues, 'hours', 'id'>;
  dayIndex: number;
};

type TimespanRowProps = {
  showRemove: boolean;
  fieldNamePrefix: `hours.${number}.times`;
  timeIndex: number;
  removeTime: UseFieldArrayRemove;
  insertTime: UseFieldArrayInsert<FormValues, `hours.${number}.times`>;
};

const TimespanRow = (props: TimespanRowProps): JSX.Element => {
  const { showRemove, fieldNamePrefix, timeIndex, removeTime, insertTime } =
    props;
  const { t } = useTranslation();
  const { control, trigger } = useFormContext<FormValues>();

  return (
    <>
      <Grid size={{ xs: 11, sm: 4 }} offset={{ sm: timeIndex === 0 ? 0 : 3 }}>
        <Controller
          name={`${fieldNamePrefix}.${timeIndex}.openTime`}
          control={control}
          render={({ field, fieldState }) => {
            return (
              <TimeField
                name={`${fieldNamePrefix}.${timeIndex}.openTime`}
                value={field.value}
                error={fieldState.error?.message}
              />
            );
          }}
        />
      </Grid>

      <Grid
        size={{ xs: 1, sm: 0.5 }}
        sx={{
          justifyContent: 'center',
          display: 'flex',
          position: 'relative',
        }}
      >
        <Typography sx={{ position: 'absolute', top: 20 }}>
          {t('createEditLocation.to')}
        </Typography>
      </Grid>

      <Grid size={{ xs: 11, sm: 4 }}>
        <Controller
          name={`${fieldNamePrefix}.${timeIndex}.closeTime`}
          control={control}
          render={({ field, fieldState }) => {
            return (
              <TimeField
                name={`${fieldNamePrefix}.${timeIndex}.closeTime`}
                value={field.value}
                error={fieldState.error?.message}
              />
            );
          }}
        />
      </Grid>

      <Grid
        size={{ xs: 1, sm: 0.5 }}
        sx={{
          justifyContent: 'center',
          display: 'flex',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: showRemove ? -6 : 12 }}>
          <IconButton
            onClick={() =>
              insertTime(timeIndex + 1, { openTime: null, closeTime: null })
            }
            aria-label={t('createEditLocation.addTime')}
            size="small"
            sx={{ ml: 1 }}
            // getByLabelText query is flaky
            data-testid="add-time-button"
          >
            <AddIcon />
          </IconButton>
          {showRemove && (
            <IconButton
              onClick={() => {
                removeTime(timeIndex);
                trigger(fieldNamePrefix);
              }}
              aria-label={t('createEditLocation.removeTime')}
              size="small"
              sx={{ ml: 1 }}
              // getByLabelText query is flaky
              data-testid="remove-time-button"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Grid>
    </>
  );
};

const HourField = (props: HourFieldProps): JSX.Element => {
  const { hourField, dayIndex } = props;
  const { control, getValues } = useFormContext<FormValues>();
  const {
    fields: timesFields,
    append: appendTime,
    replace: replaceTimes,
    remove: removeTime,
    insert: insertTime,
  } = useFieldArray({
    control,
    name: `hours.${dayIndex}.times`,
  });

  const addNewHourTimes = (): void => {
    for (let i = dayIndex - 1; i >= 0; i -= 1) {
      const prevTimes = getValues(`hours.${i}.times`) || [];
      const filteredTimes = prevTimes.filter(
        (time) =>
          typeof time.openTime === 'number' &&
          typeof time.closeTime === 'number',
      );
      if (filteredTimes.length > 0) {
        replaceTimes(filteredTimes);
        return;
      }
    }

    appendTime({ openTime: null, closeTime: null });
  };

  return (
    <Grid
      container
      spacing={2}
      alignItems="stretch"
      data-testid={`hours-${dayIndex}`}
    >
      <Grid size={{ xs: 12, sm: 3 }}>
        <Controller
          name={`hours.${dayIndex}.checked`}
          control={control}
          render={({ field }) => {
            return (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => {
                      const { checked } = e.target;
                      if (checked) {
                        addNewHourTimes();
                      } else {
                        replaceTimes([]);
                      }
                      field.onChange(checked);
                    }}
                    id={`hours.${dayIndex}.dayOfWeek`}
                  />
                }
                label={getDayOfWeek(hourField.dayOfWeek)}
                sx={{
                  '.MuiFormControlLabel-label': {
                    fontWeight: 'fontWeightMedium',
                  },
                }}
              />
            );
          }}
        />
      </Grid>

      {timesFields.map((timeField, timeIndex) => (
        <TimespanRow
          key={timeField.id}
          showRemove={timeIndex > 0}
          fieldNamePrefix={`hours.${dayIndex}.times`}
          timeIndex={timeIndex}
          removeTime={removeTime}
          insertTime={insertTime}
        />
      ))}
    </Grid>
  );
};

export const FormHours = (props: FormHoursProps): JSX.Element => {
  const { locationTimeZone } = props;
  const { t, i18n } = useTranslation();
  const { control, watch, setValue, formState } = useFormContext<FormValues>();
  const { fields: hoursFields } = useFieldArray({
    control,
    name: 'hours',
  });

  const expandHours = watch('expandHours', false);

  return (
    <>
      <Grid size={12}>
        <Button
          variant="text"
          color="inherit"
          startIcon={
            expandHours ? <KeyboardArrowDownIcon /> : <ChevronRightIcon />
          }
          onClick={() => setValue('expandHours', !expandHours)}
          disabled={formState.isSubmitting}
          aria-label={
            expandHours
              ? t('createEditLocation.collapseHours')
              : t('createEditLocation.expandHours')
          }
        >
          <Typography variant="h3">
            {t('createEditLocation.sections.hours')}
          </Typography>
        </Button>
      </Grid>

      <Grid
        size={12}
        sx={{ display: expandHours ? 'block' : 'none' }}
        data-testid="hours-section"
      >
        <Stack spacing={2} useFlexGap>
          <Typography>
            {t('createEditLocation.hoursHelperText')}
            {isValidTimeZone(locationTimeZone)
              ? ` — ${formatTimeZoneDisplayName(locationTimeZone, {
                  locale: i18n.resolvedLanguage,
                })}`
              : ''}
          </Typography>

          {hoursFields.map((hourField, dayIndex) => (
            <HourField
              key={hourField.id}
              hourField={hourField}
              dayIndex={dayIndex}
            />
          ))}
        </Stack>
      </Grid>
    </>
  );
};
